import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router';
import { useDispatch } from 'react-redux';

import { Box, Card, Typography, useTheme } from '@mui/material';

import { ColumnDatatable, FlexyDatatable } from '@europrocurement/flexy-datatable';
import { BooleanBadge } from '@europrocurement/flexy-components';

import {
    useUtilisateurService,
    useUtilisateursServiceSelector,
    utilisateursGroupesTravailSelector,
    UtilisateursAppDispatch,
} from '@europrocurement/l2d-domain';

import { JointureUtilisateurGroupeTravailJsonldUserworkgroupRead as Jointure } from '@europrocurement/l2d-domain/openApi/ApiUtilisateur';

import { utilisateursGroupesTravailPath } from '../../../constants/paths';
import { User } from '../../../types/type';
import { UserGroupesTravailActionMenu } from './UserGroupesTravailActionMenu';

type UserGroupesTravailProps = {
    user: User;
};

type Inscriptions = Jointure & Record<string, unknown>;

export const UserGroupesTravail: React.FunctionComponent<UserGroupesTravailProps> = function ({
    user,
}) {
    // Hooks
    const theme = useTheme();
    const navigate = useNavigate();
    const dispatch = useDispatch<UtilisateursAppDispatch>();
    const inscriptionsDataSource = useUtilisateursServiceSelector(
        utilisateursGroupesTravailSelector,
    );
    const { getUtilisateursGroupeTravail } = useUtilisateurService();

    // States
    const [filtersReady, setFiltersReady] = useState<boolean>(false);
    const [inscriptions, setInscriptions] = useState<Inscriptions[]>([]);
    const [historicSearch, setHistoricSearch] = useState<boolean>(false);

    // Apply standard filters.
    useEffect(() => {
        if (user) {
            // Reset/clean search parameters.
            dispatch({
                type: `${inscriptionsDataSource.slicename}/reset${inscriptionsDataSource.name}Pagination`,
                payload: {},
            });
            dispatch({
                type: `${inscriptionsDataSource.slicename}/reset${inscriptionsDataSource.name}Filter`,
                payload: {},
            });
            dispatch({
                type: `${inscriptionsDataSource.slicename}/reset${inscriptionsDataSource.name}Search`,
                payload: {},
            });

            // Set filters to fetch active only inscriptions full list
            dispatch({
                type: `${inscriptionsDataSource.slicename}/set${inscriptionsDataSource.name}Pagination`,
                payload: { page: 0, itemsPerPage: 10 },
            });
            dispatch({
                type: `${inscriptionsDataSource.slicename}/set${inscriptionsDataSource.name}Filter`,
                payload: { key: 'historicInscription', value: historicSearch },
            });
            dispatch({
                type: `${inscriptionsDataSource.slicename}/set${inscriptionsDataSource.name}Filter`,
                payload: { key: 'user', value: user.id },
            });
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [user, historicSearch]);

    // Check filters are applied
    useEffect(() => {
        if (user && inscriptionsDataSource.filters) {
            if (
                inscriptionsDataSource.filters.historicInscription === historicSearch &&
                inscriptionsDataSource.filters.user === user.id
            ) {
                setFiltersReady(true);
            }
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [inscriptionsDataSource.filters, user]);

    // dispatch data
    useEffect(() => {
        if (filtersReady) {
            dispatch(getUtilisateursGroupeTravail({}));
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [filtersReady]);

    useEffect(() => {
        if (inscriptionsDataSource.data && inscriptionsDataSource.status === 'succeeded') {
            setInscriptions(inscriptionsDataSource.data);
        }
    }, [inscriptionsDataSource.data, inscriptionsDataSource.status]);

    const columns: Array<ColumnDatatable> = [
        {
            label: 'Nom',
            field: 'libelle',
            isDisplayed: true,
            sortable: false,
            render: (inscr: Inscriptions) => inscr.groupetravail?.lib,
        },
        {
            label: 'Référent',
            render: (inscr: Inscriptions) => (
                <Typography variant="l2dcaption">
                    {inscr.groupetravail?.iduserReferent?.prenom}{' '}
                    {inscr.groupetravail?.iduserReferent?.nom}
                </Typography>
            ),
            isDisplayed: true,
            sortable: false,
            field: 'iduserReferent',
        },
        {
            label: 'Mail',
            field: 'mail',
            isDisplayed: true,
            sortable: false,
            render: (inscr: Inscriptions) => inscr.groupetravail?.email,
        },
        {
            label: "Période d'inscription",
            field: 'inscription',
            isDisplayed: true,
            sortable: false,
            render: (inscr: Inscriptions) => (
                <Box>
                    {inscr.dDebut && inscr.dFin && (
                        <Typography variant="l2dcaption">
                            Du {new Date(inscr.dDebut).toLocaleDateString('FR-fr')} au{' '}
                            {new Date(inscr.dFin).toLocaleDateString('FR-fr')}
                        </Typography>
                    )}
                    {inscr.dDebut && !inscr.dFin && (
                        <Typography variant="l2dcaption">
                            Depuis le {new Date(inscr.dDebut).toLocaleDateString('FR-fr')}
                        </Typography>
                    )}
                    {!inscr.dDebut && inscr.dFin && (
                        <Typography variant="l2dcaption">
                            Jusqu&apos;au {new Date(inscr.dFin).toLocaleDateString('FR-fr')}
                        </Typography>
                    )}
                    {!inscr.dDebut && !inscr.dFin && (
                        <Typography variant="l2dcaption">Non renseigné</Typography>
                    )}
                </Box>
            ),
        },
        {
            label: 'Actions',
            render: (inscr: Inscriptions) =>
                inscr.groupetravail?.id && (
                    <UserGroupesTravailActionMenu workgroupId={inscr.groupetravail?.id} />
                ),
            isDisplayed: true,
            displayLabel: false,
            rowReverse: true,
        },
    ];

    return (
        <Card
            data-testid="info-card"
            sx={{ width: '100%', margin: '15px 0 0 0', padding: '0' }}
        >
            <Box
                className="information-card"
                sx={{
                    backgroundColor: theme.palette.background.dark,
                    margin: '0',
                }}
            >
                <Box
                    className="heading"
                    sx={{
                        borderLeftColor: theme.palette.primary.main,
                        marginBottom: '0 !important',
                    }}
                >
                    <Box
                        className="title"
                        sx={{
                            color: theme.palette.text.primary,
                        }}
                    >
                        Inscriptions dans les groupes de travail :
                    </Box>
                    <BooleanBadge
                        label="Inscriptions actives"
                        value={!historicSearch}
                        icon
                        toggleAction={() => {
                            setHistoricSearch(!historicSearch);
                            setFiltersReady(false);
                        }}
                    />
                </Box>

                <Box
                    sx={{
                        display: 'flex',
                        justifyContent: 'center',
                        alignItems: 'center',
                        margin: 'auto',
                        width: '95%',
                    }}
                >
                    <FlexyDatatable
                        status={inscriptionsDataSource.status}
                        columns={columns}
                        data={inscriptions}
                        onClickRow={(e: React.MouseEvent<HTMLElement>, inscr: Inscriptions) => {
                            navigate(`${utilisateursGroupesTravailPath}${inscr.groupetravail?.id}`);
                        }}
                        sx={{
                            width: '100%',
                            padding: 'none',
                            margin: 'none',
                        }}
                    />
                </Box>
            </Box>
        </Card>
    );
};
export default UserGroupesTravail;
